#! /usr/bin/env node
/**
 *  @file       signal-handler.js       Utility to add intermediate POSIX signal handling. Handling is done
 *                                      for signals that terminate the process, which ensures dcpExit is emitted.
 *                                      dcpExit is introduced here as we don't want to call exit ourselves, which would
 *                                      change node behaviour in the user's persepctive. 
 *
 *
 *  @author     Kirill Kirnichansky, kirill@distributive.network
 *  @date       September, 2022
 */
'use strict';

const DCP_ENV = require('../common/dcp-env');
const debugging = require('dcp/debugging').scope('signals');

let process;
if (DCP_ENV.platform === 'nodejs')
{
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  process = requireNative('process');
}

function signalHandler(sig)
{
  // we only want to handle the signal if there are no other user defined handlers
  // this check ensures the only handler for the signal is this one
  if (process.listenerCount(sig) === 1 && process.listeners(sig)[0] === signalHandler)
  {
    process.off(sig, signalHandler);
    process.emit('dcpExit');
    process.kill(process.pid, sig);
    // this is a check for if node changes in the future
    // currently we don't expect an exit event (reason for this handler in the first place)
    process.on('exit', () => {
      const error = new Error('Process exit handler fired unexpectedly!');
      process.emitWarning(error);
      throw error;
    });
  }
}

exports.init = function init()
{
  if (init.__initialized)
    return;
  init.__initialized = true;
  exports.init = () => void 0; /* fast-path thunk */
  debugging() && console.debug('initialized signal-handler module');
  // signals that terminate process
  for (let sig of ['SIGHUP', 'SIGINT', 'SIGQUIT', 'SIGILL', 'SIGTRAP', 'SIGUSR2', 'SIGTERM', 'SIGALRM',
                   'SIGABRT', 'SIGFPE', 'SIGSEGV', 'SIGBUS', 'SIGSYS', 'SIGSTKFLT', 'SIGXCPU',
                   'SIGVTALRM', 'SIGPROF', 'SIGIO', 'SIGPWR'])
  {
    debugging('verbose') && console.debug(' - hooked', sig);
    if (process.listenerCount(sig) < 1)
      process.on(sig, signalHandler);
  }
  process.on('exit', () => process.emit('dcpExit'));
}
